import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Τι είναι το fine line ως στυλ τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Το fine line είναι ένα αισθητικό κίνημα που βασίζεται στη λεπτότητα, τον αέρα γύρω από τα στοιχεία και την αποχή από το περιττό. Δεν ορίζεται μόνο από το πάχος της γραμμής, ορίζεται από τη φιλοσοφία του «λιγότερο είναι περισσότερο»." } },
    { "@type": "Question", name: "Ποια υποκατηγορία fine line ταιριάζει για μένα;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται από το αισθητικό σου. Single needle για μέγιστη λεπτότητα, botanical για οργανικά σχέδια με ζωντανή αίσθηση, geometric για δομημένες συνθέσεις, script για γράμματα με χαρακτήρα. Ένας καλός artist θα σε βοηθήσει να βρεις ποιο ταιριάζει στο placement και στον τρόπο σου." } },
    { "@type": "Question", name: "Γιατί το fine line έγινε τόσο δημοφιλές;", acceptedAnswer: { "@type": "Answer", text: "Συνδυάστηκε με την άνοδο του minimalism και τη δύναμη του Instagram, λεπτά σχέδια φωτογραφίζονται εξαιρετικά. Παράλληλα, ανοίγει το τατουάζ σε κόσμο που θέλει κάτι διακριτικό αλλά σημαντικό." } },
  ],
};

export default function FineLineTattooAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Fine Line τατουάζ: τι ορίζει το στυλ και σε ποιον ταιριάζει",
      "description": "Τι είναι το fine line ως αισθητικό κίνημα, ποια υποκατηγορία, single needle, botanical, geometric, script, ταιριάζει σε ποιον, και γιατί έγινε τόσο δημοφιλές.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-25",
      "dateModified": "2026-04-25",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/fine-line-tattoo-athens",
      "inLanguage": "el"
    };
    inject('schema-fineline-faq', faqSchema);
    inject('schema-fineline-article', articleSchema);
    return () => {
      document.getElementById('schema-fineline-faq')?.remove();
      document.getElementById('schema-fineline-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Fine Line Τατουάζ: Τι Ορίζει το Στυλ και σε Ποιον Ταιριάζει | 210 Tattoo Athens</title>
        <meta name="description" content="Τι είναι το fine line ως αισθητικό κίνημα, ποια υποκατηγορία ταιριάζει σε σένα, και γιατί έγινε τόσο δημοφιλές στην Αθήνα." />
        <link rel="canonical" href="https://210tattoo.gr/blog/fine-line-tattoo-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/fine-line-tattoo-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/fine-line-tattoo-athens" />
        <meta property="og:title" content="Fine Line Τατουάζ: Τι Ορίζει το Στυλ και σε Ποιον Ταιριάζει | 210 Tattoo Athens" />
        <meta property="og:description" content="Τι είναι το fine line ως αισθητικό κίνημα, ποια υποκατηγορία ταιριάζει σε σένα, και γιατί έγινε τόσο δημοφιλές." />
        <meta property="og:url" content="https://210tattoo.gr/blog/fine-line-tattoo-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('fine-line-tattoo-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fine Line Τατουάζ: Στυλ και Αισθητική | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Τι ορίζει το fine line ως στυλ, ποια υποκατηγορία ταιριάζει σε σένα." />
        <meta name="twitter:image" content={ogImage('fine-line-tattoo-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Fine Line Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Fine Line:</span>
              <br />
              <span className="text-white">το στυλ, η αισθητική, το κίνημα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widests uppercase">25 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Το fine line δεν είναι απλά «λεπτές γραμμές». Είναι ένας τρόπος σκέψης γύρω από το τατουάζ, αισθητικός, φιλοσοφικός, διακριτικός. Αυτό το άρθρο εξηγεί τι το ορίζει και γιατί έγινε η επικρατέστερη επιλογή στην Αθήνα.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* What defines fine line as a style */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι ορίζει το fine line <span className="text-pink-500">ως αισθητικό κίνημα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Το fine line έχει μια φιλοσοφία πίσω του: ο αέρας γύρω από ένα σχέδιο έχει εξίσου σημασία με το σχέδιο το ίδιο. Η αποχή από το περιττό δεν είναι έλλειψη, είναι επιλογή.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αυτό το διαφέρει από το blackwork ή το traditional, όπου το γέμισμα, η τόλμη και η ορατότητα από απόσταση είναι η γλώσσα. Στο fine line, η γλώσσα είναι η λεπτομέρεια που ανακαλύπτεις από κοντά.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Λεπτότητα ως πρόθεση', desc: 'Δεν είναι απλώς μικρές ή λεπτές γραμμές, είναι μια αισθητική δέσμευση στην εύθραυστη ακρίβεια. Κάθε γραμμή έχει ένα σκοπό.' },
                { title: 'Αρνητικός χώρος ως στοιχείο', desc: 'Το τι δεν είναι εκεί έχει εξίσου βάρος. Ο αέρας γύρω από τα στοιχεία δεν είναι «άδειος», είναι μέρος του σχεδίου.' },
                { title: 'Διακριτικότητα χωρίς ασημαντότητα', desc: 'Ένα fine line tattoo μπορεί να είναι μικρό και να μην φαίνεται από μακριά, αλλά από κοντά να λέει κάτι βαθύ.' },
                { title: 'Σύγκλιση με τη σύγχρονη αισθητική', desc: 'Το fine line αντανακλά τη σύγχρονη αισθητική του minimal design, καθαρές γραμμές, σαφείς προθέσεις, τίποτα περιττό.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sub-styles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τα υποστυλ του fine line, <span className="text-pink-500">ποιο είναι το δικό σου</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το fine line δεν είναι μία κατηγορία. Έχει αναπτυχθεί σε αρκετές υποκατηγορίες, κάθε μία με διαφορετική αισθητική και διαφορετικό κοινό.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Single needle', desc: 'Η πιο ακραία έκφραση του fine line. Μία μόνο βελόνα, μέγιστη λεπτότητα, σχεδόν γραφιστική αισθητική. Ταιριάζει σε σκέτα, ελάχιστα σχέδια χωρίς γέμισμα, και απαιτεί artist με πολύ εξειδικευμένη χέρι.' },
                { title: 'Botanical / floral fine line', desc: 'Τα πιο δημοφιλή fine line σχέδια στην Αθήνα. Λουλούδια, φύλλα, βοτανικές συνθέσεις με οργανικές καμπύλες, δίνουν ζωντανή, φυσική αίσθηση. Λειτουργούν ιδιαίτερα καλά σε πλευρά, αντιβράχιο και ωμοπλάτη.' },
                { title: 'Geometric fine line', desc: 'Δομημένες συνθέσεις από γραμμές, γεωμετρικά σχήματα και συμμετρία. Το αντίθετο του organic, πιο αρχιτεκτονικό, πιο calculated. Ταιριάζει σε άτομα που τείνουν στο minimalist και το abstract.' },
                { title: 'Script / lettering fine line', desc: 'Γράμματα, λέξεις, αποσπάσματα, γραμμένα με λεπτό, χαρακτηριστικό στυλ. Δεν είναι απλώς γράμματα: είναι ο τρόπος που γράφονται που κάνει τη διαφορά. Η επιλογή γραμματοσειράς και το μέγεθος έχουν μεγάλο βάρος στο τελικό αποτέλεσμα.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why fine line exploded */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί το fine line <span className="text-pink-500">έγινε τόσο δημοφιλές</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν είναι τυχαίο ότι το fine line κυριάρχησε την τελευταία δεκαετία. Πολλοί παράγοντες συνέκλιναν ταυτόχρονα.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: 'Instagram και φωτογραφική αισθητική', desc: 'Τα fine line τατουάζ φωτογραφίζονται εξαιρετικά. Η λεπτομέρεια αναδεικνύεται σε close-up, και αυτό ταίριαξε τέλεια με την κουλτούρα του visual social media.' },
                { title: 'Άνοδος του minimalism', desc: 'Η γενική στροφή προς το minimal στον σχεδιασμό, τη μόδα και την αρχιτεκτονική έφτασε και στο τατουάζ. Το fine line ήταν η φυσική έκφρασή της.' },
                { title: 'Νέο κοινό στο τατουάζ', desc: 'Πολλοί άνθρωποι που δεν ταυτίζονταν με το bold traditional ή το blackwork βρήκαν στο fine line μια γλώσσα που μιλούσε στη δική τους αισθητική.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who fine line suits */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σε ποιον <span className="text-pink-500">ταιριάζει το fine line</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν είναι όλα τα τατουάζ για όλους, και αυτό δεν είναι κακό. Το fine line έχει ένα ξεκάθαρο αισθητικό DNA. Ταιριάζει αν:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Προτιμάς το διακριτικό', desc: 'Θέλεις κάτι που να είναι εκεί, να σημαίνει κάτι, αλλά να μην φωνάζει. Το fine line μιλά χαμηλόφωνα, σε αυτούς που κοιτάζουν από κοντά.' },
                { title: 'Σε ελκύει το minimal', desc: 'Αν στον υπόλοιπο τρόπο ζωής σου τείνεις προς το καθαρό, το απλό και το ουσιαστικό, το fine line είναι φυσική συνέχεια αυτής της αισθητικής.' },
                { title: 'Θέλεις κάτι πολύ personal', desc: 'Η λεπτότητα του fine line επιτρέπει μεγάλη προσωπικότητα σε μικρό μέγεθος. Ένα μικρό botanical σχέδιο μπορεί να λέει πολύ περισσότερα από ένα μεγάλο bold piece.' },
                { title: 'Σκέφτεσαι το placement με προσοχή', desc: 'Το fine line ανταμείβει αυτούς που σκέφτονται πού θα μπει το σχέδιο, γιατί η σχέση σχεδίου και σώματος είναι κομμάτι της αισθητικής.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How an artist thinks differently about fine line */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς σκέφτεται ο artist <span className="text-pink-500">διαφορετικά για fine line</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το fine line απαιτεί διαφορετική σχεδιαστική νοοτροπία από τα bold στυλ. Αυτές είναι οι τέσσερις βασικές αλλαγές στον τρόπο σκέψης.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Ο αέρας σχεδιάζεται, δεν μένει τυχαία', desc: 'Σε ένα bold τατουάζ, ο αέρας γύρω από τα στοιχεία είναι συχνά ουδέτερος. Στο fine line, κάθε κενό είναι σκόπιμο, καθορίζει το ρυθμό και την ισορροπία του σχεδίου.' },
                { step: '02', title: 'Το μέγεθος αλλάζει τη δομή, όχι απλώς την κλίμακα', desc: 'Ένα botanical fine line σχέδιο δεν «μικραίνει» απλώς αν το κάνεις μικρότερο. Ο artist αναδιαρθρώνει ποια στοιχεία παραμένουν και ποια αφαιρούνται για να διατηρηθεί η αναλογία.' },
                { step: '03', title: 'Η γραμμή δεν κρύβει, αποκαλύπτει', desc: 'Σε ένα blackwork κομμάτι με γέμισμα, μια ελαφρά ανωμαλία χάνεται. Στο fine line, κάθε γραμμή είναι εκτεθειμένη. Αυτό κάνει την τεχνική σταθερότητα μη διαπραγματεύσιμη.' },
                { step: '04', title: 'Η συνομιλία με τον πελάτη είναι μεγαλύτερη', desc: 'Τα fine line σχέδια απαιτούν συχνά περισσότερο consultation, γιατί οι λεπτές αποφάσεις (στυλ γραμματοσειράς, πάχος γραμμής, πυκνότητα) καθορίζουν πολύ το τελικό αποτέλεσμα.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Fine line στο <span className="text-pink-500">210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το fine line είναι από τα κύρια στυλ που δουλεύουμε. Κάθε σχέδιο ξεκινά από το concept σου, και διαμορφώνεται γύρω από το placement, το sub-style που σε εκφράζει και την αισθητική που ψάχνεις.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Sub-style consultation', desc: 'Στο consultation εξερευνούμε μαζί ποιο υποστυλ fine line ταιριάζει στο δικό σου αισθητικό, single needle, botanical, geometric ή script.' },
                { title: 'Custom σχέδιο από την αρχή', desc: 'Δεν χρησιμοποιούμε templates. Κάθε fine line σχέδιο είναι μοναδικό για σένα, το placement σου και τον τρόπο που σκέφτεσαι το τατουάζ.' },
                { title: 'Σχεδιασμός με αέρα', desc: 'Δεν γεμίζουμε το χώρο για να φαίνεται «περισσότερο». Σχεδιάζουμε με πρόθεση, και ο αέρας γύρω από το σχέδιο είναι μέρος της δουλειάς.' },
                { title: 'Ειλικρίνεια για sub-style καταλληλότητα', desc: 'Αν το sub-style που σε ενδιαφέρει δεν ταιριάζει στο placement ή τη δομή του σχεδίου που θέλεις, θα σου πούμε και θα προτείνουμε εναλλακτική.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σκέφτεσαι fine line τατουάζ <span className="text-pink-500">στην Αθήνα;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας μήνυμα στο Instagram του 210 Tattoo Athens για consultation. Δεν χρειάζεται να έχεις αποφασίσει sub-style ή σχέδιο, αρκεί μια αίσθηση αισθητικής.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/fine-line-tattoo-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Fine Line Tattoos: Ερωτήσεις για το Consultation</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να ρωτήσεις τον artist πριν κλείσεις ραντεβού για fine line, και πότε το fine line δεν είναι η σωστή επιλογή.</p>
              </Link>
              <Link to="/blog/tattoo-placement-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Οδηγός Placement Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πώς να επιλέξεις το σωστό σημείο, και πώς το σώμα επηρεάζει το design.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
